/**
 * Date and timezone utilities for consistent date formatting across the application
 */

/**
 * Formats a date string or Date object to a localized date and time string
 * @param dateInput - ISO date string or Date object
 * @param options - Formatting options
 * @returns Formatted date and time string in user's timezone
 */
export const formatDateTime = (
  dateInput: string | Date,
  options: {
    includeTime?: boolean;
    includeSeconds?: boolean;
    dateStyle?: 'short' | 'medium' | 'long' | 'full';
  } = {}
): string => {
  if (!dateInput) return 'N/A';
  
  const {
    includeTime = true,
    includeSeconds = false,
    dateStyle = 'medium'
  } = options;

  try {
    // Parse the date - if it's a string, ensure it's properly formatted
    const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;
    
    // Check if date is valid
    if (isNaN(date.getTime())) {
      console.warn('Invalid date:', dateInput);
      return 'Invalid Date';
    }

    // If only date is needed
    if (!includeTime) {
      return date.toLocaleDateString(undefined, {
        dateStyle
      });
    }

    // Format with both date and time
    const timeOptions: Intl.DateTimeFormatOptions = {
      hour: '2-digit',
      minute: '2-digit',
      ...(includeSeconds && { second: '2-digit' })
    };

    const dateStr = date.toLocaleDateString(undefined, { dateStyle });
    const timeStr = date.toLocaleTimeString(undefined, timeOptions);
    
    return `${dateStr} ${timeStr}`;
  } catch (error) {
    console.error('Error formatting date:', error, dateInput);
    return 'Invalid Date';
  }
};

/**
 * Formats a date for display in tables or lists (compact format)
 * @param dateInput - ISO date string or Date object
 * @returns Short formatted date and time
 */
export const formatCompactDateTime = (dateInput: string | Date): string => {
  return formatDateTime(dateInput, {
    dateStyle: 'short'
  });
};

/**
 * Formats a date for detailed views (full format)
 * @param dateInput - ISO date string or Date object
 * @returns Detailed formatted date and time
 */
export const formatDetailedDateTime = (dateInput: string | Date): string => {
  return formatDateTime(dateInput, {
    dateStyle: 'long'
  });
};

/**
 * Formats just the date without time
 * @param dateInput - ISO date string or Date object
 * @returns Formatted date only
 */
export const formatDate = (dateInput: string | Date): string => {
  return formatDateTime(dateInput, {
    includeTime: false,
    dateStyle: 'medium'
  });
};

/**
 * Formats just the time without date
 * @param dateInput - ISO date string or Date object
 * @returns Formatted time only
 */
export const formatTime = (dateInput: string | Date): string => {
  if (!dateInput) return 'N/A';
  
  try {
    const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;
    
    if (isNaN(date.getTime())) {
      return 'Invalid Time';
    }

    return date.toLocaleTimeString(undefined, {
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch (error) {
    console.error('Error formatting time:', error, dateInput);
    return 'Invalid Time';
  }
};

/**
 * Formats date for chart labels (short month and day)
 * @param dateInput - ISO date string or Date object
 * @returns Formatted date for charts (e.g., "Jan 15")
 */
export const formatChartDate = (dateInput: string | Date): string => {
  if (!dateInput) return '';
  
  try {
    const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;
    
    if (isNaN(date.getTime())) {
      return '';
    }

    return date.toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric'
    });
  } catch (error) {
    console.error('Error formatting chart date:', error, dateInput);
    return '';
  }
};

/**
 * Gets relative time (e.g., "2 hours ago", "3 days ago")
 * @param dateInput - ISO date string or Date object
 * @returns Relative time string
 */
export const formatRelativeTime = (dateInput: string | Date): string => {
  if (!dateInput) return 'N/A';
  
  try {
    const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;
    
    if (isNaN(date.getTime())) {
      return 'Invalid Date';
    }

    const now = new Date();
    const diffInMs = now.getTime() - date.getTime();
    const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
    const diffInHours = Math.floor(diffInMinutes / 60);
    const diffInDays = Math.floor(diffInHours / 24);

    if (diffInMinutes < 1) {
      return 'Just now';
    } else if (diffInMinutes < 60) {
      return `${diffInMinutes} minute${diffInMinutes !== 1 ? 's' : ''} ago`;
    } else if (diffInHours < 24) {
      return `${diffInHours} hour${diffInHours !== 1 ? 's' : ''} ago`;
    } else if (diffInDays < 7) {
      return `${diffInDays} day${diffInDays !== 1 ? 's' : ''} ago`;
    } else {
      // For dates older than a week, show the actual date
      return formatDate(date);
    }
  } catch (error) {
    console.error('Error formatting relative time:', error, dateInput);
    return 'Invalid Date';
  }
};
